/**
 * Copyright (C) 2020 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React, { useState, useEffect, useCallback, Dispatch } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
  selectChartsXAxisMaxZoom,
  selectChartsXAxisMinZoom,
  selectHoveredAnnotation,
  selectSelectedTerminalFlightPath,
  selectTerminalHistoryAnnotations,
  selectTooltipStepMap,
} from '../../../store';
import {
  AnnotationPoint,
  SVG_DATA_PREFIX,
  SVG_26x26,
  CLUSTER_TEXT,
  TOOLTIP_HIDE_DELAY_MS,
} from '../FullTailView/charts/ChartWrappers/TimelineChartHelperFunctioners';
import { FlightPathWaypoint } from '../../../shared/entities/terminal';
import { SEC_TO_MS_CONVERSION } from '../../../utils/DateTimeUtils';
import { setHoveredAnnotation, setTooltipStepMap } from '../../../store/ui/actions';
import FlightPathMarkerOverlayView, { FLIGHT_PATH_ANNOTATION_CUSTOM_DATA } from './FlightPathMarkerOverlayView';
import { FTV_MAP_ZOOM_BUFFER } from '../FullTailView/FullTailViewMapZoomContainer';
import isNil from 'lodash/isNil';
import { PortalsEnum } from '../../../shared/entities/portal';
import { HoveredPointPayload } from '../../../store/ui/reducer';

const MARKER_SIZE = 20;
const MARKER_SIZE_WITH_ACTIVE_RING = MARKER_SIZE + 10;

const DEFAULT_Z_INDEX = 20;
const ACTIVE_Z_INDEX = 21;

interface FlightPathMarkerContainerProps {
  map: google.maps.Map;
}

/**
 * Used as the middle man data class between finding the intersection of the flight path/annotations
 * and setting the marker on the map
 */
export interface SimpleMarker {
  lat: number;
  lng: number;
  symbol: string;
  id: number; // source time of the marker
  annotation: Partial<AnnotationPoint>;
}

const mouseOutCallback = (mouseOutTimer: number | undefined) => {
  return () => {
    return () => {
      if (mouseOutTimer) {
        clearTimeout(mouseOutTimer);
      }
    };
  };
};

export const createMarkersFromFlightPathCallback = (
  flightPath: FlightPathWaypoint[] | undefined,
  annotations: Partial<AnnotationPoint>[],
  setSimpleMarkers: React.Dispatch<React.SetStateAction<SimpleMarker[]>>,
) => {
  return () => {
    // get the markers from the annotations and flight path
    // the source time from the annotations will be cross referenced with the flight path coordinates
    if (flightPath) {
      setSimpleMarkers(
        annotations.reduce((accumMarkers: SimpleMarker[], ann: Partial<AnnotationPoint>) => {
          if (ann) {
            const sourceTime = getIdFromAnnotationPoint(ann);

            const flightPathPoint = flightPath.find((waypoint: FlightPathWaypoint) => {
              return waypoint.sourceTime === sourceTime;
            });

            if (flightPathPoint && ann.marker && ann.marker.symbol) {
              accumMarkers.push({
                lat: flightPathPoint.latitudeDeg,
                lng: flightPathPoint.longitudeDeg,
                symbol: ann.marker.symbol
                  .replace('url(', '')
                  .replace(')', '')
                  .replace(
                    encodeURIComponent(CLUSTER_TEXT),
                    encodeURIComponent(`${CLUSTER_TEXT}${ann.clusteredData ? ann.clusteredData.length : ''}`),
                  ),
                id: flightPathPoint.sourceTime,
                annotation: { ...ann },
              });
            }
          }

          return accumMarkers;
        }, []),
      );
    }
  };
};

export const convertMarkersToGoogleMarkersCallback = (
  markerMap: {
    [id: number]: google.maps.Marker;
  },
  simpleMarkers: SimpleMarker[],
  setPointMouseOutTimer: React.Dispatch<React.SetStateAction<number | undefined>>,
  setHoveredGoogleMarker: React.Dispatch<React.SetStateAction<google.maps.Marker | null>>,
  mouseLeave: () => void,
  setMarkerMap: React.Dispatch<
    React.SetStateAction<{
      [id: number]: google.maps.Marker;
    }>
  >,
) => {
  return () => {
    let shouldUpdateMarkerMap = false;

    // if there are any current markers that are not in the marker array (i.e. stale markers)
    // than delete them from the map
    let currentMap = Object.entries(markerMap).reduce(
      (accumMap, [key, marker]) => {
        const simpleMarkerInArray = simpleMarkers.find(simpleMarker => simpleMarker.id === parseInt(key));
        const icon = marker.getIcon() as google.maps.ReadonlyIcon;
        let symbolChanged = false;

        if (icon && simpleMarkerInArray) {
          symbolChanged = icon.url !== simpleMarkerInArray.symbol;
        }

        // remove stale marker from google maps and delete from the map
        if (!simpleMarkerInArray || symbolChanged) {
          shouldUpdateMarkerMap = true;
          marker.setMap(null);
          delete accumMap[key];
        }

        return accumMap;
      },
      { ...markerMap },
    );

    // go through current markers and create google map markers to show on the map
    const newMap = simpleMarkers.reduce(
      (accumMap: { [id: number]: google.maps.Marker }, simpleMarker: SimpleMarker) => {
        if (!accumMap[simpleMarker.id]) {
          const tempMarker = new google.maps.Marker({
            position: new google.maps.LatLng(simpleMarker.lat, simpleMarker.lng),
            icon: {
              url: simpleMarker.symbol,
              scaledSize: new google.maps.Size(MARKER_SIZE, MARKER_SIZE),
              anchor: new google.maps.Point(MARKER_SIZE / 2, MARKER_SIZE / 2), // center of the scaled size
            },
            zIndex: DEFAULT_Z_INDEX,
          });

          tempMarker.set(FLIGHT_PATH_ANNOTATION_CUSTOM_DATA, { ...simpleMarker.annotation });

          tempMarker.addListener('mouseover', (event: any) => {
            event.stop();

            setPointMouseOutTimer(undefined);
            setHoveredGoogleMarker(tempMarker);
          });

          tempMarker.addListener('mouseout', mouseLeave);

          accumMap[simpleMarker.id] = tempMarker;
          shouldUpdateMarkerMap = true;
        }

        return accumMap;
      },
      { ...currentMap },
    );

    if (shouldUpdateMarkerMap) {
      setMarkerMap(newMap);
    }
  };
};

// set the active marker icon to include a circle around it
export const addMarkerIconsCallback = (
  hoveredAnnotation: HoveredPointPayload,
  markerMap: {
    [id: number]: google.maps.Marker;
  },
) => {
  return () => {
    let icon: google.maps.ReadonlyIcon | null = null;
    let marker: google.maps.Marker | null = null;

    if (hoveredAnnotation && hoveredAnnotation.annotation) {
      let id = getIdFromAnnotationPoint(hoveredAnnotation.annotation);

      if (id) {
        marker = markerMap[id];

        if (marker) {
          icon = marker.getIcon() as google.maps.ReadonlyIcon;

          if (icon && icon.url) {
            marker.setIcon({
              url: `${SVG_DATA_PREFIX}${encodeURIComponent(
                // add the active circle around the marker's icon
                `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40"><circle cx="20" cy="20" r="19" fill="none" stroke="#ffffff" stroke-width="2"/>`,
              )}${icon.url.replace(SVG_DATA_PREFIX, '').replace(
                // Add the offset (x, y) for the svg based on the added circle
                encodeURIComponent(SVG_26x26),
                encodeURIComponent(
                  '<svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 26 26" x="7" y="7">',
                ),
              )}${encodeURIComponent('</svg>')}`,
              scaledSize: new google.maps.Size(MARKER_SIZE_WITH_ACTIVE_RING, MARKER_SIZE_WITH_ACTIVE_RING),
              anchor: new google.maps.Point(MARKER_SIZE_WITH_ACTIVE_RING / 2, MARKER_SIZE_WITH_ACTIVE_RING / 2), // center of the scaled size
            });

            // set the active z index to move the marker above the rest
            marker.setZIndex(ACTIVE_Z_INDEX);
          }
        }
      }
    }

    return () => {
      if (marker && marker.setIcon && marker.setZIndex && icon) {
        marker.setIcon(icon);
        marker.setZIndex(DEFAULT_Z_INDEX);
      }
    };
  };
};

export const assignMapToMarkersCallback = (
  markerMap: {
    [id: number]: google.maps.Marker;
  },
  map: google.maps.Map,
) => {
  return () => {
    Object.entries(markerMap).forEach(([key, marker]) => {
      marker.setMap(map);
    });

    return () => {
      Object.entries(markerMap).forEach(([key, marker]) => {
        marker.setMap(null);
      });
    };
  };
};

export const updateHoveredAnnotationCallback = (
  hoveredGoogleMarker: google.maps.Marker | null,
  dispatch: Dispatch<any>,
) => {
  return () => {
    if (hoveredGoogleMarker) {
      const annotation = hoveredGoogleMarker.get(FLIGHT_PATH_ANNOTATION_CUSTOM_DATA);
      dispatch(setHoveredAnnotation({ annotation, source: 'flightpath' }));
    } else {
      dispatch(setHoveredAnnotation({ annotation: null, source: 'flightpath' }));
    }
  };
};

// zoom the map when the zoom level changes on the chart
export const updateMapZoomCallback = (
  map: google.maps.Map,
  flightPath: FlightPathWaypoint[] | undefined,
  chartsXAxisMaxZoom: number | null,
  chartsXAxisMinZoom: number | null,
  setZoomToFlightPathBounds: React.Dispatch<React.SetStateAction<google.maps.LatLngBounds | null>>,
) => {
  return () => {
    if (map && flightPath && !isNil(chartsXAxisMaxZoom) && !isNil(chartsXAxisMinZoom)) {
      let newMapBounds = new google.maps.LatLngBounds();
      let foundNewBounds = false;

      flightPath.forEach((point: FlightPathWaypoint) => {
        if (
          point.sourceTime >= Math.floor(chartsXAxisMinZoom / SEC_TO_MS_CONVERSION) &&
          point.sourceTime <= Math.ceil(chartsXAxisMaxZoom / SEC_TO_MS_CONVERSION)
        ) {
          newMapBounds.extend(new google.maps.LatLng(point.latitudeDeg, point.longitudeDeg));
          foundNewBounds = true;
        }
      });
      setZoomToFlightPathBounds(currentMapBounds => {
        // only return the new map bounds if they differ
        // returning the current state value will not cause a re-render
        if (!foundNewBounds || (currentMapBounds && currentMapBounds.equals(newMapBounds))) {
          return currentMapBounds;
        }
        return newMapBounds;
      });
    } else if (map && flightPath && isNil(chartsXAxisMaxZoom) && isNil(chartsXAxisMinZoom)) {
      setZoomToFlightPathBounds(null);
    }
  };
};

export const mapFitBoundsCallback = (map: google.maps.Map, zoomToFlightPathBounds: google.maps.LatLngBounds | null) => {
  return () => {
    let currentZoomInfo: google.maps.LatLngBounds | null = null;

    if (map && zoomToFlightPathBounds) {
      currentZoomInfo = map.getBounds() as google.maps.LatLngBounds;
      map.fitBounds(zoomToFlightPathBounds, FTV_MAP_ZOOM_BUFFER);
    }

    return () => {
      if (currentZoomInfo && map) {
        map.fitBounds(currentZoomInfo, 0);
      }
    };
  };
};

const onNextMarkerCallback = (
  tooltipStepMap: {
    [key: number]: number;
  },
  hoveredAnnotation: HoveredPointPayload,
  dispatch: Dispatch<any>,
) => {
  return () => {
    const newStepMap = { ...tooltipStepMap };

    if (hoveredAnnotation && hoveredAnnotation.annotation && hoveredAnnotation.annotation.x) {
      const step = tooltipStepMap[hoveredAnnotation.annotation.x];
      newStepMap[hoveredAnnotation.annotation.x] = step ? step + 1 : 1;
    }

    dispatch(setTooltipStepMap(newStepMap));
  };
};

const onPreviousMarkerCallback = (
  tooltipStepMap: {
    [key: number]: number;
  },
  hoveredAnnotation: HoveredPointPayload,
  dispatch: Dispatch<any>,
) => {
  return () => {
    const newStepMap = { ...tooltipStepMap };

    if (hoveredAnnotation && hoveredAnnotation.annotation && hoveredAnnotation.annotation.x) {
      const step = tooltipStepMap[hoveredAnnotation.annotation.x];
      newStepMap[hoveredAnnotation.annotation.x] = step ? step - 1 : 0;
    }

    dispatch(setTooltipStepMap(newStepMap));
  };
};

export function FlightPathMarkerContainer({ map }: FlightPathMarkerContainerProps) {
  const dispatch = useDispatch();

  const [simpleMarkers, setSimpleMarkers] = useState([] as SimpleMarker[]);
  const [markerMap, setMarkerMap] = useState({} as { [id: number]: google.maps.Marker });
  const [hoveredGoogleMarker, setHoveredGoogleMarker] = useState<google.maps.Marker | null>(null);
  const [mouseOutTimer, setPointMouseOutTimer] = useState<number | undefined>(undefined);
  const [zoomToFlightPathBounds, setZoomToFlightPathBounds] = useState<null | google.maps.LatLngBounds>(null);

  const annotations = useSelector(selectTerminalHistoryAnnotations, shallowEqual);
  const flightPath = useSelector(selectSelectedTerminalFlightPath(PortalsEnum.UNIFIED_PORTAL), shallowEqual);
  const hoveredAnnotation = useSelector(selectHoveredAnnotation, shallowEqual);
  const tooltipStepMap = useSelector(selectTooltipStepMap, shallowEqual);
  const chartsXAxisMaxZoom = useSelector(selectChartsXAxisMaxZoom, shallowEqual);
  const chartsXAxisMinZoom = useSelector(selectChartsXAxisMinZoom, shallowEqual);

  const mouseLeave = useCallback(() => {
    setPointMouseOutTimer(
      setTimeout(() => {
        setHoveredGoogleMarker(null);
      }, TOOLTIP_HIDE_DELAY_MS),
    );
  }, [setHoveredGoogleMarker, setPointMouseOutTimer]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(mouseOutCallback(mouseOutTimer), [mouseOutTimer]);

  const tooltipMouseEnter = useCallback(() => {
    clearTimeout(mouseOutTimer);
  }, [mouseOutTimer]);

  const resetHoveredState = useCallback(() => {
    // only reset the hovered state if currently hovering
    if (hoveredAnnotation && hoveredAnnotation.annotation) {
      setPointMouseOutTimer(undefined);
      setHoveredGoogleMarker(null);
    }
  }, [setHoveredGoogleMarker, hoveredAnnotation]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(createMarkersFromFlightPathCallback(flightPath, annotations, setSimpleMarkers), [annotations, flightPath]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(
    convertMarkersToGoogleMarkersCallback(
      markerMap,
      simpleMarkers,
      setPointMouseOutTimer,
      setHoveredGoogleMarker,
      mouseLeave,
      setMarkerMap,
    ),
    [simpleMarkers, markerMap, mouseLeave],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(addMarkerIconsCallback(hoveredAnnotation, markerMap), [markerMap, hoveredAnnotation]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(assignMapToMarkersCallback(markerMap, map), [markerMap, map]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(updateHoveredAnnotationCallback(hoveredGoogleMarker, dispatch), [dispatch, hoveredGoogleMarker]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(updateMapZoomCallback(map, flightPath, chartsXAxisMaxZoom, chartsXAxisMinZoom, setZoomToFlightPathBounds), [
    map,
    chartsXAxisMaxZoom,
    chartsXAxisMinZoom,
    flightPath,
  ]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(mapFitBoundsCallback(map, zoomToFlightPathBounds), [map, zoomToFlightPathBounds]);

  return hoveredGoogleMarker && hoveredAnnotation && hoveredAnnotation.annotation && hoveredAnnotation.annotation.x ? (
    <FlightPathMarkerOverlayView
      marker={hoveredGoogleMarker}
      map={map}
      mouseOutHandler={resetHoveredState}
      mouseEnterHandler={tooltipMouseEnter}
      tooltipStep={tooltipStepMap[hoveredAnnotation.annotation.x]}
      onNext={onNextMarkerCallback(tooltipStepMap, hoveredAnnotation, dispatch)}
      onPrevious={onPreviousMarkerCallback(tooltipStepMap, hoveredAnnotation, dispatch)}
    />
  ) : null;
}

export default FlightPathMarkerContainer;

const getIdFromAnnotationPoint = (annotation: Partial<AnnotationPoint>) => {
  let sourceTime;

  if (annotation && annotation.x) {
    sourceTime = annotation.x / SEC_TO_MS_CONVERSION;

    if (annotation.clusteredData && annotation.clusteredData.length) {
      // get the middle of the clustered data and place the the marker there
      const index = Math.floor(annotation.clusteredData.length / 2);

      if (annotation.clusteredData[index].x) {
        sourceTime = annotation.clusteredData[index].x / SEC_TO_MS_CONVERSION;
      }
    }
  }

  return sourceTime;
};
